'use client';

import { useState } from 'react';
import {
  FileText,
  LoaderCircle,
  Plus,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Alert, AlertIcon, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { KeenIcon } from '@/components/keenicons';

const ALL_SECTIONS = '__ALL__';

type SectionValue = 'GUV' | 'AKTIVA' | 'PASSIVA' | typeof ALL_SECTIONS;
type ValueField = 'current' | 'previous' | 'both';

export function CompanyAdminActionsCard({
  euId,
  availableYears = [],
  className,
}: {
  euId: string;
  availableYears?: number[];
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const [reparsingFinancials, setReparsingFinancials] = useState(false);
  const [reparsingCD, setReparsingCD] = useState(false);

  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [section, setSection] = useState<SectionValue>(ALL_SECTIONS);
  const [valueField, setValueField] = useState<ValueField>('both');
  const [scalingFactor, setScalingFactor] = useState<string>('1');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const handleReparseFinancials = async () => {
    setReparsingFinancials(true);
    try {
      const res = await apiFetch(`/api/companies/${euId}/reparse-financials`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok) {
        toast.custom(() => (
          <Alert variant="mono" icon="success">
            <AlertIcon>
              <KeenIcon icon="check-circle" style="filled" />
            </AlertIcon>
            <AlertTitle>
              Financial reparse started (Service #{data.service_id ?? 'queued'})
            </AlertTitle>
          </Alert>
        ));
      } else {
        toast.custom(
          () => (
            <Alert variant="mono" icon="destructive">
              <AlertIcon>
                <ShieldAlert className="size-4" />
              </AlertIcon>
              <AlertTitle>
                {data.message ||
                  data.detail ||
                  data.error ||
                  'Failed to start reparse'}
              </AlertTitle>
            </Alert>
          ),
          { position: 'top-center' },
        );
      }
    } catch {
      toast.error('Failed to start financial reparse');
    } finally {
      setReparsingFinancials(false);
    }
  };

  const handleReparseCD = async () => {
    setReparsingCD(true);
    try {
      const res = await apiFetch(`/api/companies/${euId}/reparse-cd`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.custom(() => (
          <Alert variant="mono" icon="success">
            <AlertIcon>
              <KeenIcon icon="check-circle" style="filled" />
            </AlertIcon>
            <AlertTitle>CD reparsed successfully</AlertTitle>
          </Alert>
        ));
      } else {
        toast.custom(
          () => (
            <Alert variant="mono" icon="destructive">
              <AlertIcon>
                <ShieldAlert className="size-4" />
              </AlertIcon>
              <AlertTitle>
                {data.success === false
                  ? 'No CD file found for this company'
                  : data.message || 'Failed'}
              </AlertTitle>
            </Alert>
          ),
          { position: 'top-center' },
        );
      }
    } catch {
      toast.error('Failed to reparse CD');
    } finally {
      setReparsingCD(false);
    }
  };

  const resetCorrectionForm = () => {
    setSelectedYears([]);
    setSection(ALL_SECTIONS);
    setValueField('both');
    setScalingFactor('1');
    setTitleFilter('');
    setNotes('');
  };

  const handleSubmitCorrection = async () => {
    const factor = parseFloat(scalingFactor);
    if (!Number.isFinite(factor)) {
      toast.error('Scaling factor must be a number');
      return;
    }
    if (selectedYears.length === 0) {
      toast.error('Select at least one year');
      return;
    }
    const years = selectedYears.map((y) => Number(y));
    setSubmittingCorrection(true);
    try {
      const res = await apiFetch(
        `/api/companies/${euId}/financial-correction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            years,
            section: section === ALL_SECTIONS ? null : section,
            value_field: valueField,
            scaling_factor: factor,
            error_type: 'SCALING',
            title_filter: titleFilter.trim() || null,
            notes: notes.trim() || null,
          }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        const count = Array.isArray(data?.created)
          ? data.created.length
          : years.length;
        toast.custom(() => (
          <Alert variant="mono" icon="success">
            <AlertIcon>
              <KeenIcon icon="check-circle" style="filled" />
            </AlertIcon>
            <AlertTitle>
              {count === 1
                ? 'Financial correction added'
                : `${count} financial corrections added`}
            </AlertTitle>
          </Alert>
        ));
        setCorrectionOpen(false);
        resetCorrectionForm();
      } else {
        toast.custom(
          () => (
            <Alert variant="mono" icon="destructive">
              <AlertIcon>
                <ShieldAlert className="size-4" />
              </AlertIcon>
              <AlertTitle>
                {data.message || data.detail || 'Failed to add correction'}
              </AlertTitle>
            </Alert>
          ),
          { position: 'top-center' },
        );
      }
    } catch {
      toast.error('Failed to add financial correction');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  const sortedYears = [...availableYears].sort((a, b) => b - a);

  return (
    <Card
      variant="grid"
      className={cn('border-amber-200 dark:border-amber-800/50', className)}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-amber-700 dark:text-amber-400">
          <ShieldAlert className="size-4" />
          Admin Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReparseFinancials}
          disabled={reparsingFinancials}
        >
          {reparsingFinancials ? (
            <LoaderCircle className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Reparse Financials
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReparseCD}
          disabled={reparsingCD}
        >
          {reparsingCD ? (
            <LoaderCircle className="size-3.5 animate-spin" />
          ) : (
            <FileText className="size-3.5" />
          )}
          Reparse CD
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCorrectionOpen(true)}
        >
          <Plus className="size-3.5" />
          Add Financial Correction
        </Button>
      </CardContent>

      <Dialog
        open={correctionOpen}
        onOpenChange={(open) => {
          setCorrectionOpen(open);
          if (!open) resetCorrectionForm();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Financial Data Correction</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Years</Label>
                {sortedYears.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setSelectedYears(sortedYears.map((y) => String(y)))
                      }
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setSelectedYears([])}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              {sortedYears.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No financial years available for this company.
                </p>
              ) : (
                <ToggleGroup
                  type="multiple"
                  variant="outline"
                  size="sm"
                  value={selectedYears}
                  onValueChange={setSelectedYears}
                  className="flex flex-wrap justify-start gap-1"
                >
                  {sortedYears.map((y) => (
                    <ToggleGroupItem key={y} value={String(y)}>
                      {y}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fc-section">Section</Label>
                <Select
                  value={section}
                  onValueChange={(v) => setSection(v as SectionValue)}
                >
                  <SelectTrigger id="fc-section">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SECTIONS}>All sections</SelectItem>
                    <SelectItem value="GUV">GUV</SelectItem>
                    <SelectItem value="AKTIVA">AKTIVA</SelectItem>
                    <SelectItem value="PASSIVA">PASSIVA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fc-value-field">Value field</Label>
                <Select
                  value={valueField}
                  onValueChange={(v) => setValueField(v as ValueField)}
                >
                  <SelectTrigger id="fc-value-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current</SelectItem>
                    <SelectItem value="previous">Previous</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="fc-scaling-factor">Scaling factor</Label>
                <Input
                  id="fc-scaling-factor"
                  type="number"
                  step="any"
                  value={scalingFactor}
                  onChange={(e) => setScalingFactor(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fc-title-filter">Title filter (optional)</Label>
              <Input
                id="fc-title-filter"
                placeholder="e.g. Umsatzerloese"
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fc-notes">Notes (optional)</Label>
              <Textarea
                id="fc-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCorrectionOpen(false)}
              disabled={submittingCorrection}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitCorrection}
              disabled={submittingCorrection || selectedYears.length === 0}
            >
              {submittingCorrection && (
                <LoaderCircle className="size-3.5 animate-spin" />
              )}
              Add correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
